import { useClip, useClipCommits, useAssignClips } from '@/hooks/useClips'
import { useVfxShots } from '@/hooks/useVfxShots'
import { useTags } from '@/hooks/useTags'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useProject } from '@/hooks/useProjects'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { ClipRelationships } from './ClipRelationships'
import { ClipCommitAssociations } from './ClipCommitAssociations'
import { ClipTagsSection } from './ClipTagsSection'
import { ClipThumbnailUpload } from './ClipThumbnailUpload'
import { AssigneeSelector } from '@/components/shared/AssigneeSelector'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { CommentsList } from '@/components/comments/CommentsList'
import { toast } from 'sonner'
import {
  Clock,
  Film,
  HardDrive,
  Layers,
  GitCommit,
  Link2,
  UserCircle,
  CircleDot,
} from 'lucide-react'
import type { Clip } from '@/types/database'
import { formatDuration } from '@/lib/utils'
import { useAnalytics } from '@/hooks/useAnalytics'

interface ClipDetailContentProps {
  clipId: string
  projectId: string
  availableClips?: Clip[]
}

export function ClipDetailContent({ clipId, projectId, availableClips = [] }: ClipDetailContentProps) {
  const { data: clip, isLoading: clipLoading } = useClip(clipId)
  const { data: clipCommits } = useClipCommits(clipId)
  const { data: vfxShotsData } = useVfxShots(projectId)
  const { data: tags = [] } = useTags(projectId)
  const { data: project } = useProject(projectId)
  const { flags } = useFeatureFlags(projectId)
  const permissions = usePermissions(projectId, project?.owner_user_id)
  const assignClips = useAssignClips()
  const { track } = useAnalytics()

  const vfxShots = vfxShotsData?.shots || []

  const handleAssignmentChange = async (userId: string | null) => {
    try {
      await assignClips.mutateAsync({
        projectId,
        clipIds: [clipId],
        assignedUserId: userId,
      })

      // Track team member assignment
      track('team_member_assigned', {
        project_id: projectId,
        entity_type: 'clip',
        entity_id: clipId,
        assigned_user_id: userId || undefined,
        action: userId ? 'assigned' : 'unassigned',
      })

      toast.success(userId ? 'Clip assigned successfully' : 'Clip unassigned successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update assignment')
    }
  }

  if (clipLoading) {
    return <div className="text-sm text-muted-foreground">Loading clip details...</div>
  }

  if (!clip) {
    return <div className="text-sm text-destructive">Clip not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Clip Name and Thumbnail */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            {flags.enable_clip_thumbnails && (
              <div className="flex-shrink-0">
                <ClipThumbnailUpload clip={clip} projectId={projectId} />
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1">{clip.name}</h3>
              {clip.tape_name && (
                <p className="text-sm text-muted-foreground">{clip.tape_name}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tags */}
      <ClipTagsSection clip={clip} projectId={projectId} canEditTags={permissions.canEditTags} />

      {/* Status */}
      {clip.status && clip.status_type && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <CircleDot className="h-4 w-4" />
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                {clip.status_type === 'vfx_comp' ? 'VFX Comp Status' : 'Stock Footage Status'}
              </Label>
              <div>
                <StatusBadge
                  status={clip.status}
                  statusType={clip.status_type}
                  size="lg"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assignment */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <UserCircle className="h-4 w-4" />
            Assignment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Assigned To</Label>
            {permissions.canAssignWork ? (
              <AssigneeSelector
                projectId={projectId}
                value={clip.assigned_user_id}
                onValueChange={handleAssignmentChange}
                disabled={assignClips.isPending}
              />
            ) : (
              <div className="text-sm">
                {clip.assigned_user_id ? (
                  <span>Assigned</span>
                ) : (
                  <span className="text-muted-foreground">Not assigned</span>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Metadata - All clip information in one place */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Metadata
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {/* Priority fields: Start, End, Duration, Tape Name */}
            {clip.source_timecode && (
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium uppercase flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Start
                </p>
                <p className="font-medium font-mono break-words bg-muted/30 p-2 rounded">{clip.source_timecode}</p>
              </div>
            )}

            {/* End timecode from custom metadata */}
            {clip.custom_metadata?.['End'] && (
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium uppercase flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  End
                </p>
                <p className="font-medium font-mono break-words bg-muted/30 p-2 rounded">
                  {String(clip.custom_metadata['End'])}
                </p>
              </div>
            )}

            {clip.duration_frames !== null && clip.duration_frames !== undefined && (
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium uppercase flex items-center gap-1">
                  <Film className="h-3 w-3" />
                  Duration
                </p>
                <p className="font-medium font-mono break-words bg-muted/30 p-2 rounded">{formatDuration(clip.duration_frames, clip.fps)}</p>
              </div>
            )}

            {clip.tape_name && (
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium uppercase">Tape Name</p>
                <p className="font-medium break-words bg-muted/30 p-2 rounded">{clip.tape_name}</p>
              </div>
            )}

            {/* Other core fields */}
            {clip.fps && (
              <div className="space-y-1">
                <p className="text-muted-foreground font-medium uppercase">FPS</p>
                <p className="font-medium break-words bg-muted/30 p-2 rounded">{clip.fps}</p>
              </div>
            )}

            {clip.source_file_path && (
              <div className="space-y-1 col-span-2">
                <p className="text-muted-foreground font-medium uppercase flex items-center gap-1">
                  <HardDrive className="h-3 w-3" />
                  Source File Path
                </p>
                <p className="font-medium font-mono break-all bg-muted/30 p-2 rounded">
                  {clip.source_file_path}
                </p>
              </div>
            )}

            {clip.thumbnail_path && (
              <div className="space-y-1 col-span-2">
                <p className="text-muted-foreground font-medium uppercase">Thumbnail Path</p>
                <p className="font-medium font-mono break-all bg-muted/30 p-2 rounded">
                  {clip.thumbnail_path}
                </p>
              </div>
            )}

            {/* Custom metadata from OTIO */}
            {clip.custom_metadata && Object.entries(clip.custom_metadata)
              .filter(([key]) => {
                // Filter out fields that are already displayed as core structured fields
                // to avoid duplication and confusion (especially for Duration which can show incorrect raw values)
                const lowerKey = key.toLowerCase().trim()
                const coreFields = [
                  'name', 'clip name', 'clipname',
                  'tape', 'tape name', 'tapename', 'reel',
                  'start', 'source in', 'source_in', 'source timecode', 'source_timecode', 'tc in', 'tc_in',
                  'end', // Now displayed in priority section
                  'duration', 'length',
                  'fps', 'frame rate', 'framerate',
                  'source file', 'source_file', 'file path', 'filepath'
                ]
                return !coreFields.includes(lowerKey)
              })
              .map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <p className="text-muted-foreground font-medium uppercase">{key}</p>
                  <p className="font-medium break-words bg-muted/30 p-2 rounded">
                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                  </p>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Timeline Commits */}
      {clipCommits && clipCommits.associations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <GitCommit className="h-4 w-4" />
              Timeline Commits ({clipCommits.associations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ClipCommitAssociations clip={clip} />
          </CardContent>
        </Card>
      )}

      {/* Relationships */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Relationships
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ClipRelationships
            clip={clip}
            availableClips={availableClips}
            allVfxShots={vfxShots}
            allTags={tags.map(t => t.name)}
            canLink={permissions.canLinkClips}
          />
        </CardContent>
      </Card>

      {/* Comments */}
      <CommentsList
        projectId={projectId}
        commentableType="clip"
        commentableId={clipId}
      />

      {/* Timestamps */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Timestamps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Created</Label>
            <p className="text-xs">{new Date(clip.created_at).toLocaleString()}</p>
          </div>
          {clip.updated_at && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Last Updated</Label>
              <p className="text-xs">{new Date(clip.updated_at).toLocaleString()}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
